import { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';
import { Button } from '@/components/ui';
import { errorMessages } from '@/utils/errorMessages';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

const ErrorContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing.xl};
  background: ${({ theme }) => theme.colors.background};
  direction: rtl;
  text-align: center;
`;

const ErrorIcon = styled.div`
  font-size: 4rem;
  margin-bottom: ${({ theme }) => theme.spacing.lg};
  color: ${({ theme }) => theme.colors.error};
`;

const ErrorTitle = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  font-family: ${({ theme }) => theme.fonts.primary};
`;

const ErrorMessage = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.xl};
  max-width: 600px;
  font-family: ${({ theme }) => theme.fonts.primary};
  line-height: 1.6;
`;

const ErrorDetails = styled.details`
  margin-top: ${({ theme }) => theme.spacing.lg};
  padding: ${({ theme }) => theme.spacing.md};
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  max-width: 800px;
  width: 100%;
  text-align: right;
  direction: rtl;

  summary {
    cursor: pointer;
    font-weight: ${({ theme }) => theme.fontWeights.semibold};
    color: ${({ theme }) => theme.colors.textSecondary};
    margin-bottom: ${({ theme }) => theme.spacing.sm};
    font-family: ${({ theme }) => theme.fonts.primary};
  }

  pre {
    background: ${({ theme }) => theme.colors.background};
    padding: ${({ theme }) => theme.spacing.sm};
    border-radius: ${({ theme }) => theme.borderRadius.sm};
    overflow-x: auto;
    font-size: ${({ theme }) => theme.fontSizes.sm};
    text-align: left;
    direction: ltr;
    white-space: pre-wrap;
    word-break: break-word;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });

    // Log to error reporting service (e.g., Sentry)
    // logErrorToService(error, errorInfo);
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorContainer>
          <ErrorIcon>⚠️</ErrorIcon>
          <ErrorTitle>אופס! משהו השתבש</ErrorTitle>
          <ErrorMessage>
            {this.state.error?.message || errorMessages.genericError}
          </ErrorMessage>
          <ErrorMessage>
            אנחנו מצטערים על אי הנוחות. אנא נסה לרענן את הדף או לחזור לדף הבית.
          </ErrorMessage>

          <ButtonGroup>
            <Button onClick={this.handleReload} variant="primary">
              רענן דף
            </Button>
            <Button onClick={this.handleReset} variant="secondary">
              נסה שוב
            </Button>
            <Button onClick={() => (window.location.href = '/')} variant="outline">
              חזור לדף הבית
            </Button>
          </ButtonGroup>

          {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
            <ErrorDetails>
              <summary>פרטי שגיאה טכניים (למפתחים)</summary>
              <pre>
                {this.state.error?.toString()}
                {'\n\n'}
                {this.state.errorInfo.componentStack}
              </pre>
            </ErrorDetails>
          )}
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
